import { Component, OnInit } from '@angular/core';
import { ageFromDob, sexFromPatient, toOnsetDate } from 'src/app/shared/util/icd-coding-context.util';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { EMPTY, switchMap, takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { patientGroupId } from './../../../../core/utils/patient-group';
import { IcdCode } from './../../../../core/services/app-data.service';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { GroupService } from './../../../../core/services/group.service';
import { HospitalService } from './../../../../core/services/hospital.service';
import { PatientBillingService } from './../../../../core/services/patient-billing.service';
import { PatientService } from './../../../../core/services/patient.service';
import { ScrollPositionService } from './../../../../core/services/scroll-position.service';

@Component({
  selector: 'app-admit-details',
  templateUrl: './admit-details.component.html',
  styleUrls: ['./admit-details.component.scss']
})
export class AdmitDetailsComponent extends BaseComponent implements OnInit {

  // ── ICD-10 coding-rule context (shared/util/icd-coding-context.util.ts) ──────────
  //
  // Feeds the code selector's rules. Each returns null when the value is unknown, and a rule
  // with no context stays SILENT rather than guessing — a false alarm reads as the system not
  // knowing the patient, which costs trust in every other finding.
  get icdCodingSex(): string | null { return sexFromPatient(this.patient); }
  get icdCodingAge(): number | null { return ageFromDob(this.patient?.dob); }

  /** Dates a newly added code from the admission being captured — never from today. */
  get icdDefaultOnsetDate(): string | null { return toOnsetDate(this.form?.value?.admissionDate); }

  form!: FormGroup;
  patient: any;
  groupId!: string;
  hospitals: any[] = [];
  wards: any[] = [];
  selectedIcdCodes: IcdCode[] = [];
  patientAuthNumbers: any[] = [];
  visibleDiagnosisFields: string[] = [];

  todayDate: moment.Moment = moment();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private groupService: GroupService,
    private hospitalService: HospitalService,
    private patientService: PatientService,
    private patientBillingService: PatientBillingService,
    private scrollPositionService: ScrollPositionService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit() {
    this.initForm();
    const patientId = this.route.snapshot.paramMap.get('id');
    if (patientId) {
      this.patientService.getPatientById(patientId)
        .pipe(
          takeUntil(this.destroy$)
        ).subscribe(); // Trigger loading the patient
    }


    this.groupService.selectedGroup$
      .pipe(
        switchMap(selectedGroup => {
          if (selectedGroup) {
            this.groupId = selectedGroup.id;
            this.form.patchValue({ groupId: patientGroupId(this.patient, this.groupId) });
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();

    this.hospitalService.hospitals$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(hospitals => {
        this.hospitals = hospitals;
      });


    this.patientService.currentPatient$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(patient => {
        this.patient = patient;
        if (this.patient) {
          this.patchForm();
        }
        else {
          this.resetForm();
        }
      });

    this.scrollPositionService.scrollTo('top', true);
  }

  resetForm() {
    setTimeout(() => {
      this.patient = null;
      this.selectedIcdCodes = [];
      this.patientAuthNumbers = [];
      this.form.reset();
      this.initForm();
    });
  }

  patchForm() {
    if (!this.patient) {
      return; // Guard clause in case this.patient is not yet set
    }

    const data = this.patient;

    this.form.patchValue({ diagnosis: data.diagnosis, groupId: patientGroupId(data, this.groupId) });

    // Patch the diagnosis fields dynamically
    for (let i = 2; i <= 24; i++) {
      const fieldName = `diagnosis${i}`;
      if (data[fieldName]) {
        this.addDiagnosisField();
        this.form.patchValue({ [fieldName]: data[fieldName] })
      }
    }

    //this.form.patchValue(data);

    if (this.patient.icdCodes) {
      this.onIcdCodesChanged(this.patient.icdCodes);
    }

    if (this.patient.authorizationNumbers) {
      this.onAuthListChanged(this.patient.authorizationNumbers);
    }


  }

  selectHospital(event: MatSelectChange) {
    const selectedHospitalId = event.value;
    // Find the hospital object from the cached this.hospitals array
    const selectedHospital = this.hospitals.find(hospital => hospital.id === selectedHospitalId);

    if (selectedHospital && selectedHospital.wards) {
      // Sort wards alphabetically using Lodash
      this.wards = _.orderBy(selectedHospital.wards, ['name'], ['asc']);
    } else {
      // Reset wards if no hospital is found or the hospital has no wards
      this.wards = [];
    }
  }

  initForm() {
    this.form = this.fb.group({
      status: ['inpatient', Validators.required],
      groupId: [patientGroupId(this.patient, this.groupId), Validators.required],
      hospitalId: [null, Validators.required],
      wardId: [null, Validators.required],
      diagnosis: ['', Validators.required],
      admissionDate: [moment(), Validators.required],
      admissionType: ['', Validators.required],
      admissionSummary: [''],
      bed: [''],
      authorizationNumbers: [[]],
      icdCodes: [[]],
    });

    // Add diagnosis fields dynamically
    for (let i = 2; i <= 24; i++) {
      const fieldName = `diagnosis${i}`;
      this.form.addControl(fieldName, this.fb.control(''));
    }

  }

  addDiagnosisField(): void {
    const nextIndex = this.visibleDiagnosisFields.length + 2; // Start at 2 for diagnosis2
    if (nextIndex <= 24) {
      const fieldName = `diagnosis${nextIndex}`;
      this.form.addControl(fieldName, this.fb.control(''));
      this.visibleDiagnosisFields.push(fieldName);
    }
  }


  onIcdCodesChanged(codes: IcdCode[]) {
    this.selectedIcdCodes = codes;
    this.form.patchValue({ icdCodes: codes });
  }

  onAuthListChanged(authorizationNumbers: any[]) {
    const sortedAuth = _.sortBy(authorizationNumbers, (auth) => moment(auth.date)).reverse() || [];
    this.patientAuthNumbers = sortedAuth;
    this.form.patchValue({ authorizationNumbers: sortedAuth });
  }


  admitPatient() {
    const formValue = this.form.value;
    this.patientService.updatePatient(this.patient.id, formValue)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (result) => {
          await this.patientBillingService.checkMissingCoding(result.data, formValue, this.hospitals, [], this);
          this.showToastMessage('Patient admitted successfully');
          this.loadPatient(result.data.id);
        },
        error: (error) => {
          console.error('Error admitting patient: ', error);
          this.showToastMessage('There was an error admitting the patient. Please try again.');
        }
      });
  }


  loadPatient(patientId: string | null) {
    this.patientService.getPatientById(patientId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(
        result => {
          if (!patientId) {
            this.router.navigate(['/patients', {
              outlets: {
                patient: null,
              }
            }]);
            return;

          }
          this.router.navigate(['/patients', {
            outlets: {
              patient: ['file', patientId],
            }
          }]);
        }, error => {
          console.error('Error loading patient: ', error);
          this.showToastMessage('There was an error loading the patient. Please try again.');
        });
  }

  returnToParent() {
    if (this.patient && this.patient.id) {
      this.router.navigate(['/patients', { outlets: { patient: ['file', `${this.patient.id}`] } }]);
    } else {
      this.router.navigate(['/patients', { outlets: { patient: null } }]);
    }
  }

}
